import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Home, Compass, MessageSquare, UserCircle, Plane, Award, Target, Heart, Users, LayoutDashboard, Sparkles } from 'lucide-react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { API, useAuth, prefetchPage } from '@/App';
import { usePlatformFlags } from '@/hooks/usePlatformFlags';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useGuest } from '@/contexts/GuestContext';
import { roleFromToken, isAiChatAllowed } from '@/lib/roleGate';
import axios from 'axios';

const ICON_MAP = {
  Home, Compass, MessageSquare, Plane, Award, Target, Heart, Users, LayoutDashboard, Sparkles,
};

const PREFETCH_MAP = { chat: 'chat', ai: 'ai', profile: 'profile' };

const DEFAULT_TABS = [
  { key: "feed", label: "Flow", icon: "Home", to: "/flow" },
  { key: "explore", label: "Explore", icon: "Compass", to: "/explore" },
  { key: "ai", label: "AI", icon: "Sparkles", to: "/ai" },
  { key: "chat", label: "Chat", icon: "MessageSquare", to: "/messages" },
];

export default function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const guest = useGuest();
  const { isEnabled } = usePlatformFlags();
  const { isAllowed } = useFeatureFlags();
  const [configTabs, setConfigTabs] = useState(DEFAULT_TABS);

  useEffect(() => {
    axios.get(`${API}/feature-flags/bottom-nav`)
      .then(r => setConfigTabs(r.data.tabs?.length ? r.data.tabs : DEFAULT_TABS))
      .catch(() => {});
  }, []);

  const profileTo = user?.role === 'agent' ? `/agents/${user.user_id}` : user?.role === 'institution' ? `/university/${user.university_id}` : '/profile';

  const FLAG_MAP = { feed: 'feed', explore: 'colleges', visa: 'visa', rpl: 'rpl', skills: 'skills', insurance: 'insurance', rentals: 'rentals', agents: 'agents' };

  // Role gate (Feb 2026): hide the AI tab for agents/institutions.
  // Read from JWT directly so the gate fires synchronously on first
  // render — no flash of the "AI" tab for agents on cold load.
  const role = user?.role || roleFromToken();
  const aiAllowed = isAiChatAllowed(role);

  const dynamicItems = (configTabs || [])
    .map(t => ({ key: t.key, icon: ICON_MAP[t.icon] || Compass, label: t.label, to: t.key === 'dashboard' ? (user?.role === 'agent' ? '/agent' : user?.role === 'institution' ? '/institution' : '/student') : t.to, flagKey: FLAG_MAP[t.key], isAI: t.key === 'ai' }))
    .filter(item => !item.flagKey || isEnabled(item.flagKey))
    .filter(item => item.key !== 'feed' || isAllowed('social_feed'))
    .filter(item => !item.isAI || aiAllowed);

  // Place AI in center of final nav (including Profile)
  const aiIndex = dynamicItems.findIndex(i => i.isAI);
  if (aiIndex >= 0) {
    const aiItem = dynamicItems.splice(aiIndex, 1)[0];
    const totalTabs = dynamicItems.length + 1 + 1; // +1 for AI itself, +1 for Profile
    const center = Math.floor(totalTabs / 2);
    dynamicItems.splice(center, 0, aiItem);
  }

  const navItems = [...dynamicItems, { key: 'profile', icon: UserCircle, label: 'Profile', to: profileTo }];

  const [visible, setVisible] = useState(true);
  const lastY = useRef(0);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const goingDown = y > lastY.current;
      lastY.current = y;
      setVisible(!goingDown);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const hiddenPaths = ['/complete-profile', '/apply/', '/get-started', '/quick-profile', '/visa-apply'];
  if (hiddenPaths.some(p => location.pathname.startsWith(p))) return null;

  return (
    <nav className={`nav-capsule lg:hidden transition-all duration-300 ${visible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none'}`} data-testid="bottom-nav">
      <div className="flex items-center gap-1">
        {navItems.map((item) => {
          const isActive = item.label === 'Profile'
            ? location.pathname === '/profile'
            : item.label === 'Chat'
            ? location.pathname.startsWith('/messages') || location.pathname.startsWith('/chat')
            : item.label === 'AI'
            ? location.pathname === '/ai'
            : location.pathname.startsWith(item.to);

          return (
            <button
              key={item.label}
              onClick={(e) => {
                const isBlocked = guest?.isGuest && (item.to === '/dashboard' || item.to === '/messages' || item.key === 'profile' || item.key === 'chat');
                if (isBlocked) {
                  e.preventDefault();
                  guest?.requireAuth?.();
                } else {
                  navigate(item.to);
                }
              }}
              onMouseEnter={() => PREFETCH_MAP[item.key] && prefetchPage(PREFETCH_MAP[item.key])}
              onTouchStart={() => PREFETCH_MAP[item.key] && prefetchPage(PREFETCH_MAP[item.key])}
              data-testid={`bottom-nav-${item.label.toLowerCase()}`}
              className={`flex flex-col items-center justify-center w-14 h-14 rounded-2xl transition-all duration-200 ${
                isActive
                  ? item.isAI ? 'text-violet-600' : 'text-[#2F80ED]'
                  : 'text-[#6B7280] hover:text-[#0A2540]'
              }`}
            >
              {item.isAI ? (
                <div className={`w-10 h-10 rounded-xl flex flex-col items-center justify-center gap-0 ${
                  isActive
                    ? 'bg-gradient-to-br from-blue-500 via-violet-500 to-fuchsia-500 shadow-lg shadow-violet-500/25'
                    : 'bg-gradient-to-br from-blue-400/80 via-violet-400/80 to-fuchsia-400/80'
                }`}>
                  <Sparkles className="h-4 w-4 text-white" strokeWidth={2} />
                  <span className="text-white font-semibold text-[6px] leading-none mt-0.5">Unexa</span>
                </div>
              ) : (
                <item.icon className={`h-5 w-5 ${isActive ? 'text-[#2F80ED]' : ''}`} strokeWidth={isActive ? 2.5 : 1.5} />
              )}
              <span className={`text-[10px] mt-0.5 font-medium ${isActive ? (item.isAI ? 'text-violet-600' : 'text-[#2F80ED]') : ''}`}>
                {item.label}
              </span>
              {isActive && (
                <div className={`w-1 h-1 rounded-full mt-0.5 ${item.isAI ? 'bg-violet-600' : 'bg-[#2F80ED]'}`} />
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
